<template>
  <div
    v-if="hasSecondaryMenu"
    :class="
      inline
        ? 'side-menu-submenu'
        : 'h-full overflow-auto scroll-invisible w-48 flex flex-col bg-white dark:bg-slate-900 px-2 pt-4 pb-8'
    "
  >
    <!--    <account-context @toggle-accounts="toggleAccountModal" />-->
    <transition-group
      name="menu-list"
      tag="ul"
      class="list-none m-0 flex flex-col gap-2"
    >
      <secondary-nav-item
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
      />
      <secondary-nav-item
        v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
      />
      <ExportWizardNavItem
        v-if="showExportWizard"
        key="export_wizard_nav_item"
      />
      <ImportWizardNavItem
        v-if="showExportWizard"
        key="import_wizard_nav_item"
      />
    </transition-group>
  </div>
</template>
<script>
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../featureFlags';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import ExportWizardNavItem from './ExportWizardNavItem.vue';
import ImportWizardNavItem from './ImportWizardNavItem.vue';
import blocks from '@chatwoot/prosemirror-schema/src/rules/blocks';
import store from '../../../store';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
    ExportWizardNavItem,
    ImportWizardNavItem,
  },
  mixins: [hasPermissionMixin],
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentRole: {
      type: String,
      default: '',
    },
    features: {
      // d99d
      type: Object,
      default: '',
    },
    isOnChatwootCloud: {
      type: Boolean,
      default: false,
    },
    showExportWizard: {
      type: Boolean,
      default: false,
    },
    showImportWizard: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      notificationMetadata: 'notifications/getMeta',
      routePermissions: 'getRoutePermissions',
      getAccount: 'accounts/getAccount',
      globalConfig: 'globalConfig/get',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    hasSecondaryMenu() {
      return this.menuConfig.menuItems && this.menuConfig.menuItems.length;
    },
    contactCustomViews() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    accessibleMenuItems() {
      if (!this.currentRole) return [];

      let menuItemsFiltered = this.menuConfig.menuItems.filter(menuItem => {
        const routeName = menuItem.toStateName;

        if (routeName === 'email_campaigns') {
          return this.isFeatureEnabledonAccount(this.accountId, 'campaigns');
        }

        const isRoleWiseRoute =
          window.roleWiseRoutes[this.currentRole]?.includes(routeName);
        const isRoutePermitted = this.routePermissions.includes(routeName);
        const isFeatureEnabled =
          !this.features || this.features[routeName] !== false;

        return (isRoleWiseRoute || isRoutePermitted) && isFeatureEnabled;
      });

      menuItemsFiltered = menuItemsFiltered.map(item => {
        if (item.toStateName === 'email_campaigns') {
          const url = this.globalConfig.emailCampaignUrl || null;
          return {
            ...item,
            toState: frontendURL(
              `accounts/${this.accountId}/campaigns/email_campaigns`
            ),
            externalUrl: url,
            isExternal: !!url,
          };
        }
        return item;
      });

      return menuItemsFiltered;
    },

    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            onlineWA: inbox.online_wa, // d99d
            apiType: inbox.api_type, // d99d
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    labelSection() {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${label.title}`
          ),
        })),
      };
    },
    contactLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/list`),
        toStateName: 'settings_teams_list',
        newLinkRouteName: 'settings_teams_list',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
        })),
      };
    },
    foldersSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentsSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/custom_view/${view.id}`
            ),
          })),
      };
    },
    additionalSecondaryMenuItems() {
      let conversationMenuItems = [this.inboxSection];
      if (this.hasReadPermission('Labels')) {
        conversationMenuItems.push(this.labelSection);
      }
      let contactMenuItems = [this.contactLabelSection];
      if (this.teams.length) {
        conversationMenuItems = [this.teamSection, ...conversationMenuItems];
      }
      if (this.customViews.length) {
        conversationMenuItems = [this.foldersSection, ...conversationMenuItems];
      }
      if (this.contactCustomViews.length) {
        contactMenuItems = [this.contactSegmentsSection, ...contactMenuItems];
      }
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
      };
    },
  },
  mounted() {
    this.fetchUnreadCount();
  },
  methods: {
    fetchUnreadCount() {
      this.$store.dispatch('notifications/unReadCount', { filter: 'mentions' });
    },
    showAddLabelPopup() {
      this.$emit('add-label');
    },
    toggleAccountModal() {
      this.$emit('toggle-accounts');
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
  },
};
</script>
